import { create } from 'zustand';
import { reviewsApi } from '../lib/api';
import type { ReviewDoc } from '../types/review';

interface ReviewState {
  review: ReviewDoc | null;
  isLoading: boolean;
  error: string | null;
  fetch: (slug: string) => Promise<void>;
  saveAnswer: (slug: string, questionKey: string, body: { choice?: string; comment?: string }) => Promise<void>;
  addNote: (slug: string, text: string) => Promise<void>;
}

/**
 * Guards fetch against out-of-order resolution: bumped on every call, so an
 * older in-flight request that resolves after a newer one must not clobber
 * the newer result.
 */
let fetchGeneration = 0;

export const useReviewStore = create<ReviewState>((set, get) => ({
  review: null,
  isLoading: false,
  error: null,

  fetch: async (slug) => {
    const generation = ++fetchGeneration;
    set({ isLoading: true, error: null });
    try {
      const { review } = await reviewsApi.get(slug);
      if (generation !== fetchGeneration) return;
      set({ review, isLoading: false });
    } catch (err) {
      if (generation !== fetchGeneration) return;
      const status = (err as { response?: { status?: number } }).response?.status;
      set({
        review: null,
        isLoading: false,
        error: status === 404 ? 'Review not found' : 'Failed to load the review',
      });
    }
  },

  saveAnswer: async (slug, questionKey, body) => {
    const { answer } = await reviewsApi.saveAnswer(slug, questionKey, body);
    const review = get().review;
    if (!review) return;
    // The server holds one answer per user per question — mirror that here.
    const others = review.answers.filter(
      (a) => !(a.questionKey === answer.questionKey && a.user.id === answer.user.id)
    );
    set({ review: { ...review, answers: [...others, answer] } });
  },

  addNote: async (slug, text) => {
    const { note } = await reviewsApi.addNote(slug, text);
    const review = get().review;
    if (!review) return;
    set({ review: { ...review, notes: [...review.notes, note] } });
  },
}));
